<script lang="ts" setup>
import type { TFilterItem, TFilterItemRange } from '~/layers/catalog/data/types'
import type { PropType } from 'vue'

import {
  type TFilterStoring,
  type TFilterStoringRange,
  TFilterStoringType,
  useCatalogFilter,
} from '~/layers/catalog/composables/catalogFilter'

const props = defineProps({
  filter: {
    type: Object as PropType<TFilterItemRange & TFilterItem>,
    required: true,
  },
})

const service = useCatalogFilter()
const bus = useCatalogEventBus()

const { filter } = toRefs(props)
const fromValue = ref('')
const toValue = ref('')
const changed = ref(false)

const minimumFractionDigits = computed(() =>
  filter.value.code === 'weight' || filter.value.code === 'width' || filter.value.code === 'diameter' ? 2 : 0,
).value
const formattedFromValue = computed(() => {
  const value = parseFloat(fromValue.value)
  if (Number.isNaN(value)) return ''
  if (value < 999) return value
  return value.toLocaleString('ru-RU').replace(',', '.')
})

const formattedToValue = computed(() => {
  const value = parseFloat(toValue.value)
  if (Number.isNaN(value)) return ''
  if (value < 999) return value
  return value.toLocaleString('ru-RU').replace(',', '.')
})
const error = computed(() => service.errorByCode(filter.value.code))
const storeValue = () => service.get(filter.value.code) as (TFilterStoring & TFilterStoringRange) | null
const storedRange = computed(() => storeValue()?.range ?? null)

const defineValues = () => {
  fromValue.value = normalizeValue(storeValue()?.range?.from, minimumFractionDigits)
  toValue.value = normalizeValue(storeValue()?.range?.to, minimumFractionDigits)
}

const getRangePayload = () => {
  const fromNum = parseFloat(fromValue.value) || 0
  const toNum = parseFloat(toValue.value) || 0

  if (fromNum === 0 && toNum === 0) {
    fromValue.value = ''
    toValue.value = ''
    service.remove(filter.value.code)
    service.emitUpdate()
    changed.value = false

    return false
  }

  const catalogMin = filter.value.range.from || 0
  const catalogMax = filter.value.range.to || 0

  let from = 0
  let to = 0

  if (fromNum > 0) {
    if (
      (fromNum > catalogMin && fromNum > catalogMax) ||
      (fromNum > catalogMin && toNum > 0 && fromNum >= toNum) ||
      fromNum < catalogMin
    ) {
      from = catalogMin
    } else {
      from = fromNum
    }
  } else {
    from = catalogMin
  }

  if (toNum > 0) {
    if (toNum < catalogMax && toNum > catalogMin) {
      to = toNum
    } else {
      to = catalogMax
    }
  } else {
    to = catalogMax
  }

  return {
    from: from.toString(),
    to: to.toString(),
  }
}

const updateValue = () => {
  const rangePayload = getRangePayload()

  if (!rangePayload) return

  fromValue.value = rangePayload.from
  toValue.value = rangePayload.to

  service.setSilent(filter.value.code, {
    range: rangePayload,
    type: TFilterStoringType.Range,
  })

  service.emitUpdate()
  changed.value = false
}

const silentUpdate = () => {
  const rangePayload = getRangePayload()

  if (!rangePayload) return

  fromValue.value = rangePayload.from
  toValue.value = rangePayload.to

  service.setSilent(filter.value.code, {
    range: rangePayload,
    type: TFilterStoringType.Range,
  })
}

const onFiltersApply = () => {
  if (changed.value) {
    changed.value = false
    silentUpdate()
  }
}

const onFilterReset = () => {
  changed.value = false
  defineValues()
}

onMounted(() => {
  defineValues()
  bus.on('filter:updated', defineValues)
  bus.on('filter:reset', onFilterReset)
  bus.on('mobileFilters:apply', onFiltersApply)
})

onBeforeUnmount(() => {
  bus.off('filter:updated', defineValues)
  bus.off('filter:reset', onFilterReset)
  bus.off('mobileFilters:apply', onFiltersApply)
})

watch(storedRange, defineValues, { deep: true })

const changeFromHandler = (value: string) => {
  // Оставляем только цифры и одну точку (Правило 4)
  fromValue.value = value.replace(',', '.').replace(/[^\d.]/g, '')
  changed.value = true
}

const changeToHandler = (value: string) => {
  toValue.value = value.replace(',', '.').replace(/[^\d.]/g, '')
  changed.value = true
}

const maskedPlaceholder = (value: string) => {
  const number = parseFloat(value)
  return number.toLocaleString('ru-RU', { minimumFractionDigits }).replace(',', '.')
}

const beforeInput = (e: InputEvent) => {
  if (!e.data) return
  const input = e?.data?.replace(',', '.')
  const target = e.target as HTMLInputElement
  if (input && !/^[0-9.]$/.test(input)) {
    e.preventDefault()
  }
  if (input === '.' && target.value?.indexOf('.') > -1) {
    e.preventDefault()
  }
}
</script>
<template>
  <div>
    <input
      :placeholder="'ОТ ' + maskedPlaceholder(String(filter.range.from))"
      class="filters__input"
      min="0"
      :value="formattedFromValue"
      onkeypress="return event.charCode != 45"
      @beforeinput="beforeInput"
      @input="changeFromHandler(($event.target as HTMLInputElement).value)"
      @wheel.prevent
    />
    <input
      :placeholder="'ДО ' + maskedPlaceholder(String(filter.range.to))"
      class="filters__input"
      min="0"
      :value="formattedToValue"
      onkeypress="return event.charCode != 45"
      @beforeinput="beforeInput"
      @input="changeToHandler(($event.target as HTMLInputElement).value)"
      @wheel.prevent
    />

    <p v-show="error" class="filters__error-message">{{ error?.message || '' }}</p>

    <div class="filters__button-wrapper">
      <button class="filters__button filters__button_secondary filters__range-button" @click="updateValue">
        Применить
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '/assets/scss/vars' as *;

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

.filters__button-wrapper {
  margin-top: 20px;
  display: none;

  @media ($md) {
    display: block;
  }
}

.filters__range-button {
  padding: 18px;
  border: 1px solid #e3e4ea;
  color: $textColor;
}

.filters__error-message {
  color: $red;
  font-size: 11px;
  text-transform: none;
  margin-top: 10px;
}
</style>
